import { useCallback, useEffect } from "react";
import {
    getPRD4011_R01List,
    getLatestPRD4011_R01,
    patchPRD4011_R01Alarm,
} from "./Api_PRD4011_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { usePRD4011_R01Store } from "./Store_PRD4011_R01";
import { PRD4011_R01Dto, PRD4011_R01PlcStatusDto } from "./Dto_PRD4011_R01";

interface Props {
    setMessage: (message: string) => void;
}

/** 서버 시각 문자열(yyyy-MM-dd HH:mm:ss)을 ms 로 변환 */
const parseServerDateTime = (value?: string | null): number | null => {
    if (!value) return null;
    const time = new Date(value.replace(" ", "T")).getTime();
    return Number.isNaN(time) ? null : time;
};

/**
 * 에러 알림을 띄울 PLC 상태인지 판단
 * - 에러 발생 중이고
 * - 최종알림확인시간(ref02)이 없거나, 재확인시간(ref01, 분)이 지났을 때
 */
export const isPRD4011_R01AlarmTarget = (
    plcStatus: PRD4011_R01PlcStatusDto,
    serverTime: string
): boolean => {
    if (!plcStatus.isError) return false;

    const lastConfirmTime = parseServerDateTime(plcStatus.ref02);
    if (lastConfirmTime === null) return true;

    const serverTimeValue = parseServerDateTime(serverTime) ?? Date.now();
    const recheckMinutes = Number(plcStatus.ref01);
    const recheckMs =
        Number.isFinite(recheckMinutes) && recheckMinutes > 0 ? recheckMinutes * 60 * 1000 : 0;

    return serverTimeValue - lastConfirmTime >= recheckMs;
};

/** 현재 떠 있는 알림은 유지하고, 새로 띄울 PLC 에러 상태만 뒤에 추가 */
export const buildPRD4011_R01AlarmList = (
    currentAlarmList: PRD4011_R01PlcStatusDto[],
    plcStatusList: PRD4011_R01PlcStatusDto[],
    serverTime: string
): PRD4011_R01PlcStatusDto[] => {
    const openedCodes = new Set(currentAlarmList.map((alarm) => alarm.minorCd));
    const newAlarmList = plcStatusList.filter(
        (plcStatus) =>
            !openedCodes.has(plcStatus.minorCd) &&
            isPRD4011_R01AlarmTarget(plcStatus, serverTime)
    );
    return newAlarmList.length > 0 ? [...currentAlarmList, ...newAlarmList] : currentAlarmList;
};

export const Content_PRD4011_R01 = (props: Props) => {
    const { setMessage } = props;

    const {
        PRD4011_R01Filter,
        setPRD4011_R01List,
        setPRD4011_R01PlcStatusList,
        setPRD4011_R01LastDataTime,
        setIsPRD4011_R01Delayed,
        setPRD4011_R01AlarmList,
        initData,
    } = usePRD4011_R01Store();
    const { setLoadingSpinner } = useLoadingSpinnerStore();

    useFetchActionbarAuth("PRD4011_R01");

    /** 조회 응답을 Store 에 반영하고 알림 대상을 갱신 */
    const applyPRD4011_R01Data = useCallback(
        (data: PRD4011_R01Dto) => {
            const plcStatusList = (data.plcStatusList ?? []).filter(
                (plcStatus) =>
                    plcStatus.equipNm === "공급부" || plcStatus.equipNm === "배출부"
            );
            setPRD4011_R01List(data.workplaceList ?? []);
            setPRD4011_R01PlcStatusList(plcStatusList);
            setPRD4011_R01LastDataTime(data.lastDataTime ?? null);
            setIsPRD4011_R01Delayed(Boolean(data.isDelayed));

            const { PRD4011_R01AlarmList } = usePRD4011_R01Store.getState();
            setPRD4011_R01AlarmList(
                buildPRD4011_R01AlarmList(
                    PRD4011_R01AlarmList,
                    plcStatusList,
                    data.serverTime
                )
            );
        },
        [
            setPRD4011_R01List,
            setPRD4011_R01PlcStatusList,
            setPRD4011_R01LastDataTime,
            setIsPRD4011_R01Delayed,
            setPRD4011_R01AlarmList,
        ]
    );

    const fetchData = useCallback(
        async (isAutoRefresh = false) => {
            setLoadingSpinner(true);
            try {
                const response = await getPRD4011_R01List("search", PRD4011_R01Filter);
                const data = response.data.data as PRD4011_R01Dto;

                if (!data) {
                    setPRD4011_R01List([]);
                    setPRD4011_R01PlcStatusList([]);
                    showWarning("조회된 데이터가 없습니다.");
                    return;
                }

                applyPRD4011_R01Data(data);

                if (!data.workplaceList || data.workplaceList.length === 0) {
                    showWarning("조회된 데이터가 없습니다.");
                    return;
                }

                if (!isAutoRefresh) {
                    ShowSuccessFetch(setMessage, `조회 완료 (${data.workplaceList.length}건)`);
                }
            } catch (error) {
                console.error("조회 실패:", error);
                showWarning("데이터 조회에 실패했습니다.");
            } finally {
                setLoadingSpinner(false);
            }
        },
        [
            PRD4011_R01Filter,
            applyPRD4011_R01Data,
            setPRD4011_R01List,
            setPRD4011_R01PlcStatusList,
            setMessage,
            setLoadingSpinner,
        ]
    );

    const fetchLatestData = useCallback(async () => {
        try {
            const response = await getLatestPRD4011_R01(PRD4011_R01Filter.compCd);
            const data = response.data.data as PRD4011_R01Dto;
            if (data) {
                applyPRD4011_R01Data(data);
                ShowSuccessFetch(
                    setMessage,
                    `자동조회 완료 (${data.workplaceList?.length ?? 0}건)`
                );
            }
        } catch (error) {
            console.error("자동조회 실패:", error);
            showWarning("자동조회에 실패했습니다.");
        }
    }, [PRD4011_R01Filter.compCd, applyPRD4011_R01Data, setMessage]);

    /** 에러 알림 확인 — 최종알림확인시간 기록 후 알림 목록에서 제거 */
    const handleAlarmConfirmClick = useCallback(
        async (plcStatus: PRD4011_R01PlcStatusDto) => {
            try {
                const response = await patchPRD4011_R01Alarm(plcStatus);
                const updatedPlcStatus = response.data.data as
                    | PRD4011_R01PlcStatusDto
                    | undefined;

                const { PRD4011_R01AlarmList, PRD4011_R01PlcStatusList } =
                    usePRD4011_R01Store.getState();
                setPRD4011_R01AlarmList(
                    PRD4011_R01AlarmList.filter(
                        (alarm) => alarm.minorCd !== plcStatus.minorCd
                    )
                );
                if (updatedPlcStatus) {
                    setPRD4011_R01PlcStatusList(
                        PRD4011_R01PlcStatusList.map((item) =>
                            item.minorCd === plcStatus.minorCd
                                ? { ...item, ref02: updatedPlcStatus.ref02 }
                                : item
                        )
                    );
                }
            } catch (error) {
                console.error("알림 확인 실패:", error);
                showWarning("알림 확인 처리에 실패했습니다.");
            }
        },
        [setPRD4011_R01AlarmList, setPRD4011_R01PlcStatusList]
    );

    const handleInitClick = useCallback(() => {
        initData();
        setMessage("초기화되었습니다.");
    }, [initData, setMessage]);

    // 사업장·작업일자 준비 시 자동 조회
    useEffect(() => {
        if (!PRD4011_R01Filter.compCd) return;
        void fetchData(false);
    }, [PRD4011_R01Filter.compCd, PRD4011_R01Filter.searchDate, fetchData]);

    useEffect(() => {
        const { autoRefreshInterval } = PRD4011_R01Filter;
        if (autoRefreshInterval === null) return;

        const ms = autoRefreshInterval * 60 * 1000;
        const intervalId = setInterval(() => {
            void fetchLatestData();
        }, ms);

        return () => clearInterval(intervalId);
    }, [PRD4011_R01Filter.autoRefreshInterval, fetchLatestData]);

    return { fetchData, fetchLatestData, handleInitClick, handleAlarmConfirmClick };
};
